import React from "react";
import { Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FactRow from "./FactRow";
import ProjectMenuButton from "./ProjectMenuButton";
import {
    CARD_CONTENT_SX, cardHeaderSx, CARD_NAME_SX, ENTITY_CARD_SELECTED_SX, ENTITY_CARD_SX,
    CARD_ACTIONS_SX, HEADER_DELETE_SX, MONO_FONT, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, STATUS_LABEL_SX,
} from "./entityCardChrome";
import { formatDateOnly, formatDateTime } from "../helper/formatDate";

/** Maps legacy typo values stored by older backend code to canonical ExplorationStatus strings. */
export function normalizeExplorationStatus(status) {
    if (!status) return "PENDING";
    const legacy = {
        COLOR_MEASURMENT_DONE: "COLOR_MEASUREMENT_DONE",
        WIDTH_MEASUERMENT_DONE: "WIDTH_MEASUREMENT_DONE",
    };
    return legacy[status] || status;
}

const titleCase = (value) =>
    value.toLowerCase().split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");

/** Status presentation: friendly label + dot/progress-bar colour. */
export function explorationStatusPresentation(status) {
    const normalized = (status || "").toUpperCase();
    switch (normalized) {
        case "PENDING":
            return { label: "Not started", color: "#f59e0b", bar: "#e5e7eb" };
        case "FULL_CLUSTER_MARKED":
            return { label: "Marking in progress", color: "#4C7EFF", bar: "#4C7EFF" };
        case "WIDTH_MEASUREMENT_DONE":
            return { label: "Width measured", color: "#4C7EFF", bar: "#4C7EFF" };
        case "COLOR_MEASUREMENT_DONE":
            return { label: "Colour measured", color: "#16a34a", bar: "#16a34a" };
        case "COMPLETED":
            return { label: "Completed", color: "#16a34a", bar: "#16a34a" };
        default:
            return {
                label: normalized ? titleCase(normalized) : "Unknown",
                color: "#9ca3af",
                bar: "#9ca3af",
            };
    }
}

/** Marking-colour swatch for the exploration objective. */
export function objectiveSwatch(name) {
    switch (name) {
        case "black_exploration":
            return { label: "Black", sx: { bgcolor: "#111827", border: "1px solid #111827" } };
        case "white_exploration":
            return { label: "White", sx: { bgcolor: "#ffffff", border: "1px solid #d1d5db" } };
        case "chromatic_exploration":
            return {
                label: "Chromatic",
                sx: {
                    background: "conic-gradient(#f87171, #fbbf24, #34d399, #60a5fa, #a78bfa, #f87171)",
                    border: "1px solid #d1d5db",
                },
            };
        default:
            return name
                ? { label: name, sx: { bgcolor: "#e5e7eb", border: "1px solid #d1d5db" } }
                : null;
    }
}

/** True for an exploration that has not started (Start instead of Continue). */
export function explorationNotStarted(row) {
    return normalizeExplorationStatus(row.status) === "PENDING" && Number(row.current_iteration ?? 0) === 0;
}

/**
 * One exploration as the overview list and the Projects page show it.
 *
 * @param row        a row of api/exploration/data (with selection_count)
 * @param rights     { allowed, reason } from deleteRights()
 * @param onPrimary  Start / Continue
 * @param onAnalyse  the Analyse page
 * @param onDelete   opens the delete dialog
 * @param projects   the team's projects for the Move-to menu (omit: no menu)
 * @param onMoveToProject  (projectId | null) => void
 * @param showProject  the Project fact row (off on the Projects page)
 * @param selected   outlined as part of a multi-card drag
 * @param dragProps  draggable / onDragStart / onDragEnd / onClick of the card
 */
export default function ExplorationCard({
    row, rights, onPrimary, onAnalyse, onDelete,
    projects, onMoveToProject, showProject = true, selected = false, dragProps = {},
}) {
    const status = normalizeExplorationStatus(row.status);
    const presentation = explorationStatusPresentation(status);
    const swatch = objectiveSwatch(row.exploration_objective_name);
    const currentIteration = Number(row.current_iteration ?? 0);
    const totalIterations = Number(row.iteration_count ?? 0) || null;
    const progress = totalIterations
        ? Math.min(100, Math.max(0, (100 * currentIteration) / totalIterations))
        : 0;
    const notStarted = explorationNotStarted(row);
    const inUse = row.selection_count > 0;
    const deleteTitle = inUse
        ? `Used by ${row.selection_count} selection${row.selection_count === 1 ? "" : "s"} — cannot be deleted`
        : rights?.reason || "Delete exploration";

    return (
        <Card
            variant="outlined"
            data-kind="exploration"
            data-id={row.id}
            sx={{ ...ENTITY_CARD_SX, ...(selected ? ENTITY_CARD_SELECTED_SX : {}) }}
            {...dragProps}
        >
            {/* dark header band: status + actions + name */}
            <Box sx={cardHeaderSx("exploration")}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                        title={`Status: ${titleCase(row.status || "")}`}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: presentation.color, flexShrink: 0 }} />
                        <Typography variant="body2" sx={STATUS_LABEL_SX}>
                            {presentation.label}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" sx={{ mr: -0.5, flexShrink: 0 }}>
                        <ProjectMenuButton
                            projects={projects}
                            currentProjectId={row.project || null}
                            onMove={onMoveToProject}
                            itemLabel="exploration"
                        />
                        <Tooltip title={deleteTitle}>
                            {/* span keeps the tooltip working on a disabled button */}
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={inUse || !rights?.allowed}
                                    onClick={onDelete}
                                    aria-label="delete exploration"
                                    sx={HEADER_DELETE_SX}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Typography component="div" sx={CARD_NAME_SX}>
                    {row.name}
                </Typography>
            </Box>

            <CardContent sx={CARD_CONTENT_SX}>
                <Stack spacing={0} sx={{ flex: 1 }}>
                    {/* iterations + progress */}
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                            Iterations
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                fontFamily: MONO_FONT,
                                fontSize: "0.76rem",
                                color: notStarted ? "text.secondary" : "primary.main",
                            }}
                        >
                            {currentIteration}
                            {totalIterations ? ` / ${totalIterations}` : ""}
                        </Typography>
                    </Stack>
                    <Box
                        sx={{ height: 4, borderRadius: 999, bgcolor: "rgba(15, 23, 42, 0.08)", overflow: "hidden", mb: 1 }}
                        aria-label={`Iteration progress: ${currentIteration}${totalIterations ? ` of ${totalIterations}` : ""}`}
                    >
                        <Box
                            sx={{
                                width: `${progress}%`, height: "100%", borderRadius: 999,
                                bgcolor: presentation.bar, transition: "width 0.3s ease",
                            }}
                        />
                    </Box>

                    {/* facts */}
                    <FactRow dense
                        label="Substrate"
                        value={row.material_name || "—"}
                        title={row.material_name ? `Substrate: ${row.material_name}` : undefined}
                    />
                    <FactRow dense
                        label="Laser device"
                        value={row.laser_device_name || "—"}
                        title={row.laser_device_name ? `Laser: ${row.laser_device_name}` : undefined}
                    />
                    {swatch ? (
                        <FactRow dense label="Marking colour">
                            <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                title={row.exploration_objective_name ? `Objective: ${row.exploration_objective_name}` : undefined}
                            >
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, flexShrink: 0, ...swatch.sx }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {swatch.label}
                                </Typography>
                            </Stack>
                        </FactRow>
                    ) : null}
                    {row.started_by_email ? (
                        <FactRow dense label="Executor" value={row.started_by_email} title={`Started by ${row.started_by_email}`} />
                    ) : null}
                    <FactRow dense label="Created" value={formatDateOnly(row.created_at)} title={formatDateTime(row.created_at)} />
                    {showProject ? (
                        <FactRow dense label="Project" value={row.project_name || "—"} />
                    ) : null}

                    {/* actions */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={CARD_ACTIONS_SX}>
                        <Button onClick={onPrimary} variant="contained" size="small" disableElevation sx={PRIMARY_BUTTON_SX}>
                            {notStarted ? "Start" : "Continue"}
                        </Button>
                        <Button onClick={onAnalyse} variant="outlined" size="small" sx={SECONDARY_BUTTON_SX}>
                            Analyse
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
